import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Form, Table, ProgressBar } from 'react-bootstrap';
import { toast } from 'react-toastify';

const PDFExtractorModal = ({ 
  show, 
  onHide, 
  onProductsExtracted 
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedProducts, setExtractedProducts] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pdfjsLoaded, setPdfjsLoaded] = useState(false);

  // Verificar se PDF.js está carregado
  useEffect(() => {
    const checkPDFJS = () => {
      if (window.pdfjsLib) {
        setPdfjsLoaded(true);
        if (!window.pdfjsLib.GlobalWorkerOptions.workerSrc) {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
        }
      } else {
        setPdfjsLoaded(false);
      }
    };

    checkPDFJS();

    if (!window.pdfjsLib) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
          'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
        setPdfjsLoaded(true);
      };
      script.onerror = () => {
        console.error('Falha ao carregar PDF.js');
        setPdfjsLoaded(false);
      };
      document.head.appendChild(script);
    }
  }, []);

  // Extrair texto do PDF
  const extractTextFromPDF = async (file) => {
    if (!window.pdfjsLib) {
      throw new Error('PDF.js não está carregado.');
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = window.pdfjsLib.getDocument({ 
        data: arrayBuffer,
        useWorkerFetch: false,
        isEvalSupported: false,
        useSystemFonts: true
      });

      const pdf = await loadingTask.promise;
      let fullText = '';

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const textItems = textContent.items || [];
        const pageText = textItems
          .filter(item => item.str && item.str.trim().length > 0)
          .map(item => item.str)
          .join(' ');
        fullText += pageText + '\n';

        setProgress((pageNum / pdf.numPages) * 70);
      }

      if (fullText.length < 10) {
        throw new Error('Nenhum texto foi extraído do PDF');
      }

      return fullText;
    } catch (error) {
      console.error('Erro ao extrair texto:', error);
      throw new Error(`Falha ao processar o PDF: ${error.message}`);
    }
  };

  // Parser para PDFs do Mercado Livre
  const parseMLData = (text) => {
    const products = [];

    const patterns = [
      /Código\s+ML:\s*([A-Z0-9]+)[\s\S]*?Código\s+universal:\s*([0-9]+)[\s\S]*?SKU:\s*([0-9]+)[\s\S]*?([A-Za-z0-9\s\-\/\|\(\)\.,:]+?)(?=Código\s+ML:|PRODUTO\s+UNIDADES|Etiquetagem|$)/gi,
      /SKU:\s*([0-9]+)[\s\S]*?([A-Za-z0-9\s\-\/\|\(\)\.,:]{20,100})(?=SKU:|Código|PRODUTO|Etiquetagem|$)/gi,
      /(?:Código|SKU|Item).*?([0-9]{4,8})[\s\S]*?([A-Za-z][A-Za-z0-9\s\-\/\|\(\)\.,:]{25,150})/gi
    ];

    for (let patternIndex = 0; patternIndex < patterns.length; patternIndex++) {
      const pattern = patterns[patternIndex];
      let match;

      while ((match = pattern.exec(text)) !== null) {
        let produto = null;

        if (patternIndex === 0 && match.length >= 5) {
          const [, codigoML, codigoUniversal, sku, descricao] = match;
          produto = {
            id: `ml_${sku}_${Date.now()}`,
            codigo: sku,
            codigo_ml: codigoML,
            codigo_universal: codigoUniversal,
            descricao: descricao.replace(/\s+/g, ' ').trim(),
            quantidade: 1,
            origem: 'ML_PDF'
          };
         } //else if (match.length >= 3) {
        //   const codigo = match[1];
        //   const descricao = match[2];
        //   if (codigo && descricao && descricao.length > 10) {
        //     produto = {
        //       id: `pdf_${codigo}_${Date.now()}`,
        //       codigo: codigo,
        //       descricao: descricao.replace(/\s+/g, ' ').trim(),
        //       quantidade: 1,
        //       origem: 'PDF_AUTO'
        //     };
        //   }
        // }

        if (produto) {
          produto.descricao = produto.descricao
            .replace(/Etiquetagem\s*obrigatória/gi, '')
            .replace(/Código\s+ML:.*$/gi, '')
            .replace(/SKU:.*$/gi, '')
            .replace(/^\s*[\|\-\•]\s*/, '')
            .replace(/\s*[\|\-\•]\s*$/, '')
            .trim();

          if (produto.descricao.length >= 10 &&
              !products.find(p => p.codigo === produto.codigo)) {
            products.push(produto);
          }
        }
      }
    }

    // 🔹 Ajuste: procurar quantidades na seção "PRODUTO UNIDADES"
    const sectionMatch = text.split(/PRODUTO\s+UNIDADES/i)[1];
    if (sectionMatch) {
      const quantities = sectionMatch
        .split(/\s+/)
        .map(q => parseInt(q))
        .filter(q => !isNaN(q) && q > 0 && q < 100000);

      quantities.forEach((qty, i) => {
        if (products[i]) {
          products[i].quantidade = qty;
        }
      });
    }

    console.log(`Produtos extraídos: ${products.length}`, products);
    return products;
  };

  // Parser genérico
  const parseGenericPDF = (text) => {
    const products = [];
    const lines = text.split('\n').filter(line => line.trim().length > 10);

    for (const line of lines) {
      const codeMatch = line.match(/\b(\d{4,8})\b/);
      if (codeMatch) {
        const codigo = codeMatch[1];
        let descricao = line.replace(codeMatch[0], '').trim();
        descricao = descricao
          .replace(/^\s*[\-\•\|]\s*/, '')
          .replace(/\s*[\-\•\|]\s*$/, '')
          .substring(0, 100)
          .trim();

        if (descricao.length >= 10) {
          products.push({
            id: `gen_${codigo}_${Date.now()}`,
            codigo: codigo,
            descricao: descricao,
            quantidade: 1,
            origem: 'PDF_GENERICO'
          });
        }
      }
    }

    return products;
  };

  // Processar PDF
  const processFile = async () => {
    if (!selectedFile) {
      toast.error('Selecione um arquivo PDF');
      return;
    }

    if (!pdfjsLoaded) {
      toast.error('PDF.js ainda não foi carregado.');
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    try {
      setProgress(10);
      const text = await extractTextFromPDF(selectedFile);
      setProgress(80);

      let products = [];
      if (text.includes('Código ML:') || text.includes('Mercado Livre') ||
          selectedFile.name.toLowerCase().includes('ml')) {
        products = parseMLData(text);
      } else {
        products = parseGenericPDF(text);
      }

      setProgress(90);

      if (products.length === 0) {
        toast.warning('Nenhum produto foi encontrado no PDF.');
        setProgress(100);
        setIsProcessing(false);
        return;
      }

      setExtractedProducts(products);
      setShowPreview(true);
      setProgress(100);
      toast.success(`${products.length} produtos encontrados no PDF!`);
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      toast.error(`Erro ao processar o PDF: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Confirmar importação
  const confirmImport = () => {
    const validProducts = extractedProducts.filter(p => 
      p.descricao && p.descricao.trim().length > 5 &&
      p.codigo && p.codigo.trim().length > 0
    );

    if (validProducts.length === 0) {
      toast.error('Nenhum produto válido para importar');
      return;
    }

    onProductsExtracted(validProducts);
    toast.success(`${validProducts.length} produtos importados com sucesso!`);
    handleClose();
  };

  const handleClose = () => {
    setSelectedFile(null);
    setExtractedProducts([]);
    setShowPreview(false);
    setProgress(0);
    setIsProcessing(false);
    onHide();
  };

  const updateProduct = (index, field, value) => {
    const updatedProducts = [...extractedProducts];
    updatedProducts[index][field] = value;
    setExtractedProducts(updatedProducts);
  };

  const removeProduct = (index) => {
    const updatedProducts = extractedProducts.filter((_, i) => i !== index);
    setExtractedProducts(updatedProducts);
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-file-pdf me-2"></i>
          Importar Produtos do PDF
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {!showPreview ? (
          <>
            {!pdfjsLoaded && (
              <Alert variant="warning">
                <i className="bi bi-exclamation-triangle me-2"></i>
                PDF.js está sendo carregado...
              </Alert>
            )}

            <Alert variant="info">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Formatos suportados:</strong>
              <ul className="mb-0 mt-2">
                <li>PDFs do Mercado Livre (ML) com SKU e descrições</li>
                <li>PDFs com formato SKU + Descrição</li>
                <li>Outros PDFs estruturados</li>
              </ul>
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label>
                <i className="bi bi-upload me-2"></i>
                Selecione o arquivo PDF
              </Form.Label>
              <Form.Control
                type="file"
                accept=".pdf"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                disabled={isProcessing || !pdfjsLoaded}
              />
              {selectedFile && (
                <Form.Text className="text-muted">
                  Arquivo: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </Form.Text>
              )}
            </Form.Group>

            {isProcessing && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Processando PDF...</span>
                  <span>{progress.toFixed(0)}%</span>
                </div>
                <ProgressBar now={progress} animated striped variant="primary" />
              </div>
            )}

            <div className="d-flex gap-2">
              <Button 
                variant="primary" 
                onClick={processFile}
                disabled={!selectedFile || isProcessing || !pdfjsLoaded}
              >
                {isProcessing ? (
                  <>
                    <i className="bi bi-arrow-repeat me-2"></i>
                    Processando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-search me-2"></i>
                    Extrair Produtos
                  </>
                )}
              </Button>

              <Button variant="secondary" onClick={handleClose}>
                Cancelar
              </Button>
            </div>
          </>
        ) : (
          <>
            <Alert variant="success">
              <i className="bi bi-check-circle me-2"></i>
              <strong>{extractedProducts.length} produtos encontrados!</strong>
            </Alert>

            <div className="table-responsive" style={{ maxHeight: '400px' }}>
              <Table striped bordered hover size="sm">
                <thead className="table-dark sticky-top">
                  <tr>
                    <th>SKU</th>
                    <th>Descrição</th>
                    <th>Qtd</th>
                    <th>Observações</th>
                    <th>Origem</th>
                    <th>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {extractedProducts.map((product, index) => (
                    <tr key={product.id}>
                      <td>
                        <Form.Control
                          size="sm"
                          value={product.codigo}
                          onChange={(e) => updateProduct(index, 'codigo', e.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          as="textarea"
                          rows={2}
                          value={product.descricao}
                          onChange={(e) => updateProduct(index, 'descricao', e.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          min="1"
                          value={product.quantidade}
                          onChange={(e) => updateProduct(index, 'quantidade', parseInt(e.target.value) || 1)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          value={product.obs || ''}
                          onChange={(e) => updateProduct(index, 'obs', e.target.value)}
                          placeholder="Observações..."
                        />
                      </td>
                      <td>
                        <span className={`badge ${
                          product.origem === 'ML_PDF' ? 'bg-success' :
                          product.origem === 'PDF_AUTO' ? 'bg-primary' : 'bg-secondary'
                        }`}>
                          {product.origem}
                        </span>
                      </td>
                      <td>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => removeProduct(index)}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-between mt-3">
              <Button variant="outline-secondary" onClick={() => setShowPreview(false)}>
                <i className="bi bi-arrow-left me-2"></i>
                Voltar
              </Button>

              <div className="d-flex gap-2">
                <Button variant="secondary" onClick={handleClose}>
                  Cancelar
                </Button>
                <Button variant="success" onClick={confirmImport} disabled={extractedProducts.length === 0}>
                  <i className="bi bi-check-circle me-2"></i>
                  Importar {extractedProducts.length} Produtos
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PDFExtractorModal;
